import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Chip, Link, Paper, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';

import { routes } from '@app/routes';
import { CalloutBanner } from '@components/CalloutBanner';
import { useGetAssessmentById } from '@features/assessment';
import { RemediationRunStatus } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import {
  ArrowRightIcon,
  CaretRightIcon,
  CheckCircleIcon,
  ClockIcon,
  PlusIcon,
  SparkleIcon,
} from '@phosphor-icons/react';

import {
  useAppendRemediationItems,
  useRemediationRun,
  useRemediationScope,
  useTriggerRemediation,
} from '../api/remediation-api';
import { RemediationScopeSkeleton } from '../components/RemediationSkeletons';
import { ScopeAssessmentSummary } from '../components/ScopeAssessmentSummary';
import { GenerateSection, RemediateSection } from '../components/ScopeSection';
import { outstandingCandidates } from '../utils/outstanding-candidates';

/**
 * Rough runtime the design quotes in its footer: improving an existing document
 * is cheaper than writing one from scratch. Presentational only — the backend
 * reports no estimate — so it is deliberately shown as "~N min".
 */
const MINUTES_PER_REMEDIATE = 3;
const MINUTES_PER_GENERATE = 6;

/**
 * Screen 3 — assessment detail / document selection.
 *
 * Two sections (uploads to improve, documents to create), each row selectable,
 * with a sticky footer carrying the running count, a time estimate and the
 * start action. Selection is real: the trigger endpoint takes the picked ids,
 * so a user can run three documents instead of seventeen.
 */
export const RemediationScopePage: FC = observer(() => {
  const theme = useTheme();
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  const { data: scope, isLoading, isError } = useRemediationScope(assessmentId);
  const { data: assessment } = useGetAssessmentById({ assessmentId: assessmentId ?? '' });
  const trigger = useTriggerRemediation();

  const [selRemediate, setSelRemediate] = useState<Set<string>>(new Set());
  const [selGenerate, setSelGenerate] = useState<Set<string>>(new Set());

  /**
   * A run in flight owns the whole assessment lineage — the trigger endpoint
   * answers a second one with 409 HAS_ACTIVE_RUN, so this screen cannot *start*
   * one while it lasts.
   *
   * It used to bounce the user to the run for that reason. That made this page
   * unreachable for the entire life of a run, and it is the only place documents
   * can be picked — so "go back to scope and remediate the ones that were left
   * out" had no answer, and a Remediate button that silently landed on a
   * progress screen looked broken rather than busy.
   *
   * The picker stays open instead. What changes is the verb: with a run in
   * flight the selection is appended to it rather than starting a second one,
   * which is exactly what the run's payload endpoint was made to allow. The
   * banner carries the way into the run for anyone who came here to look.
   */
  const activeRun = scope?.activeRun ?? null;
  // Not FINALIZING: the endpoint would take it, but by then the approved
  // documents are being rendered and the next version forked, and appending
  // would start a second execution alongside that.
  const appendable =
    activeRun?.status === RemediationRunStatus.PENDING ||
    activeRun?.status === RemediationRunStatus.PROCESSING ||
    activeRun?.status === RemediationRunStatus.READY_FOR_REVIEW;
  const append = useAppendRemediationItems(activeRun?.runId ?? '', assessmentId ?? '');

  /**
   * What the run does not already hold.
   *
   * Only fetched while a run is in flight, because only then is anything
   * already taken. Offering a document the run has is offering a no-op — the
   * backend drops it and reports nothing added — and the user cannot tell that
   * apart from the document they actually meant to add.
   */
  const { data: activeRunDetail } = useRemediationRun(activeRun?.runId);
  const candidates = useMemo(
    () => (activeRun ? outstandingCandidates(scope, activeRunDetail?.items ?? []) : null),
    [activeRun, scope, activeRunDetail],
  );
  const remediateItems = candidates?.remediate ?? scope?.remediate ?? [];
  const generateItems = candidates?.generate ?? scope?.generate ?? [];

  // Everything starts selected: the common case is "fix all of it", and an
  // empty selection would put the primary action behind N clicks.
  //
  // Except with a run already going, where the same gesture means something
  // else. There the user came to add the one document that was left out, and a
  // pre-ticked list would make the easy click the one that re-submits the whole
  // scope — most of it dropped as duplicates, the rest queued unasked.
  useEffect(() => {
    if (!scope) return;
    if (scope.activeRun) {
      setSelRemediate(new Set());
      setSelGenerate(new Set());
      return;
    }
    setSelRemediate(new Set(scope.remediate.map((r) => r.sourceAssessmentDomainDocumentId)));
    setSelGenerate(new Set(scope.generate.map((g) => g.domainStandardDocumentId)));
  }, [scope]);

  const toggle = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  const totalSelected = selRemediate.size + selGenerate.size;
  const estimatedMinutes =
    selRemediate.size * MINUTES_PER_REMEDIATE + selGenerate.size * MINUTES_PER_GENERATE;

  const back = () =>
    navigate(assessmentId ? routes.assessmentOverview(assessmentId) : routes.remediationList());

  const onStart = () => {
    if (!assessmentId || totalSelected === 0) return;

    // With a run already in flight the selection joins it. Starting a second one
    // is refused by the trigger endpoint anyway (409 HAS_ACTIVE_RUN), so the
    // alternative to this is not "two runs" — it is the user being told no.
    if (activeRun && appendable) {
      append.mutate(
        {
          runId: activeRun.runId,
          generateSlotIds: [...selGenerate],
          remediateDocumentIds: [...selRemediate],
        },
        {
          onSuccess: ({ added, dispatch }) => {
            // `added` is what actually landed, which is lower than what was
            // ticked when some of it is already in the run — the backend drops
            // duplicates rather than rejecting the request.
            enqueueSnackbar(
              added === 0
                ? t('app.remediation.toast.addedNone')
                : dispatch === 'exhausted'
                  ? t('app.remediation.toast.addedExhausted', { count: added })
                  : t('app.remediation.toast.added', { count: added }),
              {
                variant: added === 0 ? 'info' : dispatch === 'exhausted' ? 'warning' : 'success',
              },
            );
            if (added > 0) navigate(routes.remediationRun(activeRun.runId));
          },
        },
      );
      return;
    }

    trigger.mutate(
      {
        assessmentId,
        generateSlotIds: [...selGenerate],
        remediateDocumentIds: [...selRemediate],
      },
      {
        onSuccess: ({ runId }) => {
          enqueueSnackbar(t('app.remediation.toast.started'), { variant: 'success' });
          // Pushed, not replaced: a partial selection is normal, and the user
          // must be able to come back here to see what was left out.
          navigate(routes.remediationRun(runId));
        },
      },
    );
  };

  const nothingToDo = scope && generateItems.length === 0 && remediateItems.length === 0;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.paper',
        borderRadius: 4,
        overflow: 'hidden',
      }}
    >
      {/* ── header ──────────────────────────────────────────────────────── */}
      <Box
        sx={{ px: 3, py: 2, borderBottom: `1px solid ${theme.palette.grey[300]}`, flexShrink: 0 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Link
            component="button"
            type="button"
            onClick={() => navigate(routes.remediationList())}
            sx={{ fontSize: 12.5, color: 'text.secondary', textDecoration: 'none' }}
          >
            {t('app.remediation.list.title')}
          </Link>
          <CaretRightIcon size={13} color={theme.palette.text.tertiary} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 500 }} noWrap>
            {assessment?.title ?? ''}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
          <Typography variant="h6">{t('app.remediation.scope.title')}</Typography>
          {assessment && <Chip size="small" label={`v${assessment.version}`} />}
        </Box>

      </Box>

      {/* ── body ────────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, md: 5 }, py: 2.5 }}>
        <Box
          // Full width, as in the design — the selection rows are wide tables.
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {isLoading && <RemediationScopeSkeleton />}

          {/* The scope endpoint 400s for anything ineligible — not completed, no
              results, no gaps — and there is no useful distinction to draw
              between those client-side.

              With a run in flight there is a second way to arrive here empty,
              and it is not the same fact: the gaps exist, the run already holds
              all of them. Saying "nothing to remediate" there would deny the
              documents the user can see on the run page, and `back` would send
              them to the assessment rather than to the run they came from. */}
          {!isLoading && (isError || nothingToDo) && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 4,
                  mx: 'auto',
                  mb: 2.25,
                  backgroundColor: 'green.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckCircleIcon size={32} color={theme.palette.green[800]} />
              </Box>
              <Typography variant="textMD_medium" sx={{ display: 'block', mb: 1 }}>
                {t(
                  activeRun
                    ? 'app.remediation.scope.empty.inRunTitle'
                    : 'app.remediation.scope.empty.title',
                )}
              </Typography>
              <Typography
                variant="textSM_regular"
                sx={{
                  color: 'text.secondary',
                  maxWidth: 440,
                  mx: 'auto',
                  display: 'block',
                  mb: 2.75,
                }}
              >
                {t(
                  activeRun
                    ? 'app.remediation.scope.empty.inRunBody'
                    : 'app.remediation.scope.empty.body',
                )}
              </Typography>
              {activeRun ? (
                <Button
                  variant="secondary"
                  endIcon={<ArrowRightIcon size={15} />}
                  onClick={() => navigate(routes.remediationRun(activeRun.runId))}
                >
                  {t('app.remediation.scope.activeRun.open')}
                </Button>
              ) : (
                <Button variant="secondary" onClick={back}>
                  {t('app.remediation.scope.backToAssessment')}
                </Button>
              )}
            </Box>
          )}

          {scope && !nothingToDo && (
            <>
              {/* The run in flight, and the way into it. This is what replaced
                  the redirect: the same fact, said on the page the user asked
                  for instead of by moving them off it. */}
              {activeRun && (
                <CalloutBanner
                  tone="ai"
                  density="compact"
                  filledIcon
                  title={t('app.remediation.scope.activeRun.title')}
                  action={
                    <Button
                      variant="secondary"
                      endIcon={<ArrowRightIcon size={15} />}
                      onClick={() => navigate(routes.remediationRun(activeRun.runId))}
                    >
                      {t('app.remediation.scope.activeRun.open')}
                    </Button>
                  }
                >
                  {t(
                    appendable
                      ? 'app.remediation.scope.activeRun.bodyAppend'
                      : 'app.remediation.scope.activeRun.bodyBusy',
                  )}
                </CalloutBanner>
              )}

              {/* Above the selection, because the counts below only mean
                  something next to what was assessed. */}
              {assessment && (
                <ScopeAssessmentSummary
                  assessment={assessment}
                  language={i18n.language}
                  generateCount={scope.generate.length}
                  remediateCount={scope.remediate.length}
                  onViewAssessment={back}
                />
              )}

              {/* Advisory: a thin profile means more placeholders in the output,
                  never a blocked run. */}
              {scope.profileNote.totalFields > 0 && scope.profileNote.progressPct < 100 && (
                <CalloutBanner
                  tone="warning"
                  density="compact"
                  filledIcon
                  title={t('app.remediation.scope.profileNote.title', {
                    pct: Math.round(scope.profileNote.progressPct),
                  })}
                  action={
                    <Button variant="secondary" onClick={() => navigate('/tenant-profile')}>
                      {t('app.remediation.scope.profileNote.openProfile')}
                    </Button>
                  }
                >
                  {t('app.remediation.scope.profileNote.body', {
                    filled: scope.profileNote.fieldsFilled,
                    total: scope.profileNote.totalFields,
                  })}
                </CalloutBanner>
              )}

              <RemediateSection
                items={remediateItems}
                selected={selRemediate}
                onToggle={(id) => setSelRemediate((s) => toggle(s, id))}
                onToggleAll={() =>
                  setSelRemediate((s) =>
                    s.size === remediateItems.length
                      ? new Set()
                      : new Set(remediateItems.map((r) => r.sourceAssessmentDomainDocumentId)),
                  )
                }
              />

              <GenerateSection
                items={generateItems}
                selected={selGenerate}
                onToggle={(id) => setSelGenerate((s) => toggle(s, id))}
                onToggleAll={() =>
                  setSelGenerate((s) =>
                    s.size === generateItems.length
                      ? new Set()
                      : new Set(generateItems.map((g) => g.domainStandardDocumentId)),
                  )
                }
              />
            </>
          )}
        </Box>
      </Box>

      {/* ── sticky selection footer ─────────────────────────────────────── */}
      {scope && !nothingToDo && (
        <Paper
          elevation={0}
          sx={{
            flexShrink: 0,
            borderTop: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: 0,
            boxShadow: '0 -4px 16px rgba(0,0,0,0.05)',
            px: 3,
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2.5,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.75, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {totalSelected === 0 ? (
                <Box component="span" sx={{ color: 'text.tertiary', fontWeight: 500 }}>
                  {t('app.remediation.scope.footNothing')}
                </Box>
              ) : (
                <>
                  <Box component="span" sx={{ color: 'primary.main' }}>
                    {t('app.remediation.scope.footImprove', { count: selRemediate.size })}
                  </Box>
                  <Box component="span" sx={{ color: 'text.tertiary', fontWeight: 400, mx: 1 }}>
                    ·
                  </Box>
                  <Box component="span" sx={{ color: 'ocean.700' }}>
                    {t('app.remediation.scope.footCreate', { count: selGenerate.size })}
                  </Box>
                </>
              )}
            </Typography>
            {totalSelected > 0 && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  fontSize: 12.5,
                  color: 'text.secondary',
                }}
              >
                <ClockIcon size={15} color={theme.palette.text.tertiary} />
                {t('app.remediation.scope.footTime', { minutes: estimatedMinutes })}
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1.25, flexShrink: 0 }}>
            <Button variant="tertiary" onClick={back}>
              {t('app.remediation.scope.cancel')}
            </Button>
            {/* One control, two verbs. With a run in flight the selection is
                added to it; otherwise it starts one. A run that is finalizing
                can take neither, so the control says why rather than sitting
                enabled and failing on the request. */}
            <Button
              variant="ai"
              startIcon={
                activeRun ? <PlusIcon size={17} /> : <SparkleIcon size={18} weight="fill" />
              }
              disabled={
                totalSelected === 0 ||
                trigger.isPending ||
                append.isPending ||
                Boolean(activeRun && !appendable)
              }
              onClick={onStart}
            >
              {t(
                trigger.isPending || append.isPending
                  ? activeRun
                    ? 'app.remediation.scope.adding'
                    : 'app.remediation.scope.starting'
                  : activeRun
                    ? 'app.remediation.scope.addToRun'
                    : 'app.remediation.scope.start',
              )}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
});
